import { useParams } from 'react-router-dom';

import { logError } from 'lib/sentry/logError';
import { Icon, iconNames } from 'shared_DEPRECATED/components/Icon';
import { updateArrayById } from 'shared_DEPRECATED/utils';

import { ChallengeTimelineItemType } from 'features/challenge/ChallengeTimelineItem';
import { useAddCustomChallenge } from 'features/challenge/hooks/useAddCustomChallenge';
import {
  IGoal,
  updateChallengeWithLinkedGoal,
  useGoalQueryActions,
} from 'features/goal';
import { useLastSprint } from 'features/sprint/hooks/useLastSprint';

import { ButtonIcon } from 'shared/components/Button';

type TAddChallengeButton = {
  focusAreaId: string;
};

export const AddChallengeButton = ({ focusAreaId }: TAddChallengeButton) => {
  const { goalId } = useParams();
  const sprint = useLastSprint();

  const {
    cancelGoalQueries,
    getGoalQuery,
    updateGoalQueryData,
    invalidateGoalQuery,
  } = useGoalQueryActions();

  const onCreateGoalChallengeBtnClick =
    useAddCustomChallenge<ChallengeTimelineItemType>({
      //@ts-ignore
      sprint,
      options: {
        onMutate: async ({ challenges }) => {
          cancelGoalQueries();

          updateGoalQueryData((oldData: IGoal) => {
            const focusArea = oldData.focusAreas.find(
              (focusArea) => focusArea.focusAreaId === focusAreaId
            );

            if (!focusArea) {
              return oldData;
            }

            focusArea.challenges = [
              //because the useAddCustomChallenge hook can receive a single challenge or an array of challenges
              ...(Array.isArray(challenges)
                ? challenges.map((challenge) =>
                    updateChallengeWithLinkedGoal(challenge, oldData)
                  )
                : [updateChallengeWithLinkedGoal(challenges, oldData)]),
              ...focusArea.challenges,
            ];

            return oldData;
          });

          const focusArea = getGoalQuery()?.focusAreas.find(
            (focusArea) => focusArea.focusAreaId === focusAreaId
          );

          return { challenges: focusArea?.challenges ?? [] };
        },
        onSuccess: async ({ challenges }) => {
          updateGoalQueryData((oldData: IGoal) => {
            const focusArea = oldData.focusAreas.find(
              (focusArea) => focusArea.focusAreaId === focusAreaId
            );

            if (!focusArea) {
              return oldData;
            }

            focusArea.challenges = updateArrayById({
              array: focusArea.challenges,
              updatedItem: challenges[0],
              idProperty: 'challengeId',
            });
          });
        },
        onError: async (
          error,
          __,
          context: { challenges: ChallengeTimelineItemType[] } | undefined
        ) => {
          if (!context?.challenges) {
            return;
          }

          updateGoalQueryData((oldData: IGoal) => {
            const focusArea = oldData.focusAreas.find(
              (focusArea) => focusArea.focusAreaId === focusAreaId
            );

            if (!focusArea) {
              return oldData;
            }

            focusArea.challenges = [
              ...focusArea.challenges,
              ...context.challenges,
            ];
          });

          logError(error);
        },
        onSettled: async () => {
          invalidateGoalQuery();
        },
      },
      parameters: {
        addToGoalId: goalId,
        addToGoalFocusAreaId: focusAreaId,
      },
    });

  return (
    <ButtonIcon
      aria-label="add challenge"
      onClick={onCreateGoalChallengeBtnClick}
      size="2xs"
      variant="ghost"
    >
      <Icon width="15px" height="15px" name={iconNames.plus} />
    </ButtonIcon>
  );
};
